import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Upload, 
  FileSpreadsheet, 
  FolderOpen, 
  Send, 
  Package, 
  Loader2, 
  Trash2,
  RefreshCw,
  Image,
  Filter,
  Share2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import FeatureGuard from '../components/FeatureGuard';

function ProductUploadContent({ selectedTeam }) {
  // Upload state
  const [excelFile, setExcelFile] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  // Products state
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Filter state
  const [brandFilter, setBrandFilter] = useState('all');
  const [brands, setBrands] = useState([]);

  // Publish state
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [publishing, setPublishing] = useState(false);

  // Dialog state
  const [showAnotherChannelDialog, setShowAnotherChannelDialog] = useState(false);
  const [lastPublishResult, setLastPublishResult] = useState(null);

  // UI state
  const [showUploadSection, setShowUploadSection] = useState(true);

  useEffect(() => {
    if (selectedTeam) {
      // Don't load products from database on page load - prevents lag
      // Products will be loaded from upload session
      loadChannels();
    }
  }, [selectedTeam]);

  const loadProducts = async () => {
    // This function now only loads from database when explicitly needed
    // (e.g., when user returns to continue publishing saved products)
    setLoadingProducts(true);
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/products`);
      const loadedProducts = res.data.products || [];
      if (loadedProducts.length > 0) {
        setProducts(loadedProducts);
        const uniqueBrands = [...new Set(loadedProducts.map(p => p.brand).filter(Boolean))];
        setBrands(uniqueBrands);
      }
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadChannels = async () => {
    try {
      const [teamRes, brandsRes, categoriesRes] = await Promise.all([
        axiosInstance.get(`/teams/${selectedTeam}`),
        axiosInstance.get(`/teams/${selectedTeam}/brands`),
        axiosInstance.get(`/teams/${selectedTeam}/categories`)
      ]);

      const channelList = [];
      
      if (teamRes.data.channel_id) {
        channelList.push({
          id: teamRes.data.channel_id,
          name: '📢 Main Channel',
          type: 'main'
        });
      }

      brandsRes.data.forEach(brand => {
        if (brand.channel_id) {
          channelList.push({
            id: brand.channel_id,
            name: `🏷️ ${brand.name}`,
            type: 'brand'
          });
        }
      });

      categoriesRes.data.forEach(category => {
        if (category.channel_id) {
          channelList.push({
            id: category.channel_id,
            name: `📂 ${category.name}`,
            type: 'category'
          });
        }
      });

      setChannels(channelList);
      if (channelList.length > 0) {
        setSelectedChannel(channelList[0].id);
      }
    } catch (err) {
      console.error('Error loading channels:', err);
    }
  };

  const handleExcelSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Only Excel files (.xlsx, .xls) are supported');
      return;
    }

    setExcelFile(file);
    toast.success('Excel file selected');
  };

  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files || []);
    
    const validFiles = files.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'mp4', 'mov', 'avi', 'webp'].includes(ext);
    });

    setFolderFiles(validFiles);
    toast.success(`${validFiles.length} files selected`);
  };

  // PARALLEL CHUNKED UPLOAD - Upload multiple files simultaneously for speed
  const handleUpload = async () => {
    if (!excelFile || folderFiles.length === 0) {
      toast.error('Please select Excel and media files');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Starting upload...');

    try {
      // STEP 1: Upload Excel and start session
      setUploadStatus('Processing Excel file...');
      
      const excelFormData = new FormData();
      excelFormData.append('excel', excelFile);
      
      let sessionId;
      try {
        const startRes = await axiosInstance.post(
          `/teams/${selectedTeam}/products/chunked/start`,
          excelFormData,
          { 
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000 
          }
        );
        sessionId = startRes.data.session_id;
        setUploadStatus(`Excel processed: ${startRes.data.total_products} products found`);
        toast.success(`Excel processed: ${startRes.data.total_products} products`);
      } catch (excelErr) {
        console.error('Excel upload error:', excelErr);
        toast.error(`Excel error: ${excelErr.response?.data?.detail || excelErr.message}`);
        setUploading(false);
        return;
      }

      // STEP 2: Upload media files IN PARALLEL (10 concurrent uploads)
      const CONCURRENT_UPLOADS = 10; // Number of parallel uploads
      const totalFiles = folderFiles.length;
      let completedCount = 0;
      let successCount = 0;
      let failCount = 0;

      // Helper function to upload a single file
      const uploadSingleFile = async (file) => {
        try {
          const mediaFormData = new FormData();
          mediaFormData.append('session_id', sessionId);
          mediaFormData.append('media', file);
          
          await axiosInstance.post(
            `/teams/${selectedTeam}/products/chunked/upload-media`,
            mediaFormData,
            { 
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 300000 // 5 min per file for large videos
            }
          );
          
          successCount++;
          return true;
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
          failCount++;
          return false;
        } finally {
          completedCount++;
          const progress = Math.round((completedCount / totalFiles) * 100);
          setUploadProgress(progress);
          setUploadStatus(`Uploading: ${completedCount}/${totalFiles} files (${progress}%)`);
        }
      };

      // Process files in parallel batches
      const fileArray = Array.from(folderFiles);
      for (let i = 0; i < fileArray.length; i += CONCURRENT_UPLOADS) {
        const batch = fileArray.slice(i, i + CONCURRENT_UPLOADS);
        const batchNum = Math.floor(i / CONCURRENT_UPLOADS) + 1;
        const totalBatches = Math.ceil(fileArray.length / CONCURRENT_UPLOADS);
        
        setUploadStatus(`Uploading batch ${batchNum}/${totalBatches} (${batch.length} files)...`);
        
        // Upload all files in this batch simultaneously
        await Promise.all(batch.map(file => uploadSingleFile(file)));
      }

      // STEP 3: Finish - get products (NOT saved to database yet)
      setUploadStatus('Processing products...');
      
      try {
        const finishRes = await axiosInstance.post(
          `/teams/${selectedTeam}/products/chunked/finish`,
          { session_id: sessionId },
          { timeout: 120000 }
        );
        
        const savedCount = finishRes.data.saved_count || 0;
        const returnedProducts = finishRes.data.products || [];
        
        if (failCount > 0) {
          toast.warning(`${savedCount} products loaded (${failCount} files failed)`);
        } else {
          toast.success(`${savedCount} products loaded successfully!`);
        }
        
        // Set products directly in state (not from database)
        setProducts(returnedProducts);
        const uniqueBrands = [...new Set(returnedProducts.map(p => p.brand).filter(Boolean))];
        setBrands(uniqueBrands);
        
        // Reset form
        setExcelFile(null);
        setFolderFiles([]);
        setUploadProgress(0);
        setUploadStatus('');
        
        // Clear file inputs
        document.querySelectorAll('input[type="file"]').forEach(input => {
          input.value = '';
        });
        
      } catch (finishErr) {
        console.error('Finish error:', finishErr);
        toast.error(`Save error: ${finishErr.response?.data?.detail || finishErr.message}`);
      }

    } catch (err) {
      console.error('Upload error:', err);
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
      setUploadStatus('');
    }
  };

  const handlePublish = async () => {
    if (selectedProducts.size === 0) {
      toast.error('Please select products to publish');
      return;
    }

    if (!selectedChannel) {
      toast.error('Please select a channel');
      return;
    }

    setPublishing(true);

    try {
      const productIds = Array.from(selectedProducts);
      // Get selected products data to send directly
      const selectedProductsData = products.filter(p => productIds.includes(p.id));
      
      const res = await axiosInstance.post(`/teams/${selectedTeam}/products/publish-selected`, {
        product_ids: productIds,
        products: selectedProductsData,  // Send products data directly
        channel_id: selectedChannel
      }, { timeout: 3600000 });

      setLastPublishResult(res.data);
      
      if (res.data.published_count > 0) {
        toast.success(`${res.data.published_count} product(s) published!`);
      }
      
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} error(s) occurred`);
      }
      
      setShowAnotherChannelDialog(true);

    } catch (err) {
      console.error('Publish error:', err);
      toast.error(err.response?.data?.detail || 'Publishing failed');
    } finally {
      setPublishing(false);
    }
  };

  const handleAnotherChannelResponse = async (wantAnother) => {
    setShowAnotherChannelDialog(false);
    
    if (wantAnother) {
      // Save products to database for later use
      try {
        await axiosInstance.post(`/teams/${selectedTeam}/products/save`, {
          products: products
        });
        toast.info('Products saved. Select another channel to publish.');
      } catch (err) {
        console.error('Error saving products:', err);
      }
      setSelectedProducts(new Set());
      setSelectAll(false);
    } else {
      // Just clear local state - don't save to database
      clearLocalProducts();
    }
  };

  const clearLocalProducts = () => {
    setProducts([]);
    setSelectedProducts(new Set());
    setSelectAll(false);
    setBrands([]);
    toast.success('Products cleared');
  };

  const clearAllProducts = async () => {
    try {
      // Clear from database if they were saved
      const productIds = products.map(p => p.id);
      if (productIds.length > 0) {
        await axiosInstance.post(`/teams/${selectedTeam}/products/clear`, {
          product_ids: productIds
        }).catch(() => {}); // Ignore if not in database
      }
      
      clearLocalProducts();
    } catch (err) {
      // Even if database clear fails, clear local state
      clearLocalProducts();
    }
  };

  const handleClearAll = () => {
    if (products.length === 0) {
      toast.info('No products to clear');
      return;
    }
    
    if (window.confirm(`Delete ${products.length} product(s)?`)) {
      clearAllProducts();
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    } else {
      setSelectedProducts(new Set());
    }
  };

  const handleProductSelect = (productId, checked) => {
    const newSelected = new Set(selectedProducts);
    if (checked) {
      newSelected.add(productId);
    } else {
      newSelected.delete(productId);
    }
    setSelectedProducts(newSelected);
    setSelectAll(newSelected.size === filteredProducts.length);
  };

  const filteredProducts = brandFilter === 'all' 
    ? products 
    : products.filter(p => p.brand === brandFilter);

  if (!selectedTeam) {
    return (
      <div className="p-8 text-center">
        <Package className="w-16 h-16 mx-auto mb-4 text-purple-500" />
        <h2 className="text-xl font-semibold mb-2">Select Team</h2>
        <p className="text-gray-600">Please select a team first</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="product-upload-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Product Management
        </h1>
        <p className="text-gray-600">Upload products and publish to Telegram</p>
      </div>

      {/* Upload Section */}
      <Card className="mb-6">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setShowUploadSection(!showUploadSection)}
        >
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-purple-600" />
                Upload Products
              </CardTitle>
              <CardDescription>Upload Excel and media files</CardDescription>
            </div>
            {showUploadSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </CardHeader>
        
        {showUploadSection && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="mb-2 block">Excel File</Label>
                <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-purple-500 transition-colors bg-gray-50">
                  <FileSpreadsheet className="w-8 h-8 mb-2 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {excelFile ? excelFile.name : 'Select Excel file'}
                  </span>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleExcelSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>

              <div>
                <Label className="mb-2 block">Media Folder</Label>
                <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-purple-500 transition-colors bg-gray-50">
                  <FolderOpen className="w-8 h-8 mb-2 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {folderFiles.length > 0 ? `${folderFiles.length} files selected` : 'Select folder'}
                  </span>
                  <input
                    type="file"
                    webkitdirectory=""
                    directory=""
                    multiple
                    onChange={handleFolderSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>

            {/* Upload Progress */}
            {uploading && (
              <div className="mt-4 p-4 bg-purple-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-purple-700 font-medium">{uploadStatus}</span>
                  <span className="text-sm font-bold text-purple-700">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-purple-200 rounded-full h-3">
                  <div 
                    className="bg-purple-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            <Button
              onClick={handleUpload}
              disabled={!excelFile || folderFiles.length === 0 || uploading}
              className="mt-4 bg-purple-600 hover:bg-purple-700"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Products
                </>
              )}
            </Button>
          </CardContent>
        )}
      </Card>

      {/* Products List */}
      {products.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-600" />
                  Products ({products.length})
                </CardTitle>
                <CardDescription>Select products to publish</CardDescription>
              </div>
              
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={loadProducts} disabled={loadingProducts}>
                  <RefreshCw className={`w-4 h-4 mr-1 ${loadingProducts ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAll}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-4 p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <Select value={brandFilter} onValueChange={setBrandFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by Brand" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brands</SelectItem>
                    {brands.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="selectAll"
                  checked={selectAll}
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="selectAll" className="cursor-pointer">
                  Select All ({filteredProducts.length})
                </Label>
              </div>

              {selectedProducts.size > 0 && (
                <span className="text-sm text-purple-600 font-medium ml-auto">
                  {selectedProducts.size} selected
                </span>
              )}
            </div>

            {/* Products Grid */}
            {loadingProducts ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 max-h-[400px] overflow-y-auto p-1">
                {filteredProducts.map(product => (
                  <div
                    key={product.id}
                    className={`relative rounded-xl overflow-hidden border-2 cursor-pointer transition-all ${
                      selectedProducts.has(product.id) 
                        ? 'border-purple-500 shadow-lg' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleProductSelect(product.id, !selectedProducts.has(product.id))}
                  >
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.barcode}
                        className="w-full h-24 object-cover"
                      />
                    ) : (
                      <div className="w-full h-24 bg-gray-100 flex items-center justify-center">
                        <Image className="w-8 h-8 text-gray-300" />
                      </div>
                    )}
                    
                    <div className="p-2">
                      <p className="text-xs font-medium truncate">{product.brand || 'No Brand'}</p>
                      <p className="text-[10px] text-gray-500 truncate">{product.barcode}</p>
                      {product.has_both && (
                        <span className="text-[9px] bg-green-100 text-green-700 px-1 rounded">+Video</span>
                      )}
                    </div>

                    {selectedProducts.has(product.id) && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-purple-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Publish Section */}
            <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
              <h3 className="font-semibold text-purple-800 mb-4 flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Publish Selected
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-sm text-gray-600 mb-1 block">Channel</Label>
                  <Select value={selectedChannel} onValueChange={setSelectedChannel}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Channel" />
                    </SelectTrigger>
                    <SelectContent>
                      {channels.map(channel => (
                        <SelectItem key={channel.id} value={channel.id}>
                          {channel.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button
                    onClick={handlePublish}
                    disabled={selectedProducts.size === 0 || !selectedChannel || publishing}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    {publishing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Publishing...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Publish {selectedProducts.size} Product(s)
                      </>
                    )}
                  </Button>
                </div>
              </div>
              
              <p className="text-xs text-purple-600">
                💡 Photos and videos with the same barcode will be sent as a media group.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {products.length === 0 && !loadingProducts && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No products uploaded yet</p>
              <p className="text-sm">Upload Excel and media files above</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Another Channel Dialog */}
      <Dialog open={showAnotherChannelDialog} onOpenChange={setShowAnotherChannelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Publish to Another Channel?</DialogTitle>
            <DialogDescription>
              {lastPublishResult && (
                <span className="block mt-2">
                  {lastPublishResult.published_count} product(s) published.
                </span>
              )}
              <span className="block mt-2">
                Would you like to publish to another channel?
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => handleAnotherChannelResponse(false)}>
              No, Clear Products
            </Button>
            <Button onClick={() => handleAnotherChannelResponse(true)} className="bg-purple-600 hover:bg-purple-700">
              Yes, Another Channel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Wrapper with FeatureGuard
export default function ProductUpload({ selectedTeam }) {
  return (
    <FeatureGuard featureKey="product_upload" pageName="Ürün Yükleme">
      <ProductUploadContent selectedTeam={selectedTeam} />
    </FeatureGuard>
  );
}
